import { format, formatISO, isSameDay } from 'date-fns'
import uniqueId from 'lodash/uniqueId'
import { useMemo, useRef, useState } from 'react'
import type { DragEvent, DragEventHandler, VFC } from 'react'
import { useSelector } from 'react-redux'
import { MessageKeys, ThemeProvider, useWorkspace } from '@opallabs/hooks'
import { kuiperClassNames } from '@opallabs/kuiper'
import { Tooltip } from '@opallabs/lapidary'
import {
  AnalyticsConsumer,
  AnalyticsLayer,
  AllowedEvents,
  TrackEvent,
} from '@opallabs/quanta'
import { State } from '@opallabs/wormhole/types'
import { useContentDragContext, useIsHoveredDate } from '../ContentDragContext'
import { isDropTarget } from '../helpers'
import { useMomentDragContext } from '../MomentDragContext'
import { isDateOutsideMomentRange } from '../useIsContentOutsideMomentRange'
import { useMomentMap } from '../useMomentMap'
import { formatDateKey } from '../useMomentMap/momentMap'
import * as styles from './styles'
import type { WeekViewDayHeaderProps } from './types'
import { buildFilterTreeMap, getSelectedFilterParams } from './utils'

export const WeekViewDayHeader: VFC<WeekViewDayHeaderProps> = ({
  date,
  onUpdateMoment,
  theme = 'light',
}) => {
  const { slug } = useWorkspace()
  const { draggedMoment, setDraggedMoment } = useMomentDragContext()
  const [isActiveDropTarget, setIsActiveDropTarget] = useState(false)
  const [isForbiddenDropTarget, setIsForbiddenDropTarget] = useState(false)

  const htmlId = useRef<string>(uniqueId('continuum-weekviewdayheader'))
  const filterTreeMap = useSelector((state: State) =>
    buildFilterTreeMap(state.filters.filterTree)
  )
  const selectedFilters = useSelector(
    (state: State) => state.filters.selectedFilters || {}
  )
  const selectedFilterParams = getSelectedFilterParams(
    filterTreeMap,
    selectedFilters
  )

  const { dayCounts } = useMomentMap()
  const momentCount = dayCounts.get(formatDateKey(date)) || 0
  const tooltipText = momentCount === 1 ? '1 Moment' : `${momentCount} Moments`

  const onDragEnter: DragEventHandler = () => {
    if (isDropTarget(date, draggedMoment)) {
      setIsActiveDropTarget(true)
    } else {
      setIsForbiddenDropTarget(true)
    }
  }

  const onDragLeave: DragEventHandler = () => {
    setIsActiveDropTarget(false)
    setIsForbiddenDropTarget(false)
  }

  const onDragOver: DragEventHandler = (e) => {
    if (isForbiddenDropTarget) return
    e.preventDefault()
  }

  const onDrop = (e: DragEvent, track: TrackEvent) => {
    e.preventDefault()

    const update = {
      scheduled_at: formatISO(date, { representation: 'date' }),
    }

    if (typeof draggedMoment?.id === 'string') {
      track(AllowedEvents.drop, { id: draggedMoment.id, type: 'moment' })
      setIsActiveDropTarget(false)
      onUpdateMoment({ momentId: draggedMoment.id, update })
      setDraggedMoment(null)
    }
  }

  const checkSameDay = () => {
    if (!draggedMoment) return false
    let momentDayAndTime = draggedMoment.scheduledAt
    if (typeof draggedMoment.scheduledAt === 'string') {
      // if a date string has a Z timezone indicator JS Date objects
      // may try to convert the date string to the users timezone,
      // which could cause the actual date to change which
      // we don't want
      momentDayAndTime = draggedMoment.scheduledAt.split('Z')[0]
    }
    return !isSameDay(new Date(momentDayAndTime), date)
  }

  const renderDropTargets = () => {
    if (draggedMoment && checkSameDay()) {
      return (
        <AnalyticsLayer name="Day Drop Target">
          <AnalyticsConsumer>
            {({ track }) => (
              <div
                className={kuiperClassNames('dropTarget')}
                css={styles.dropTarget({
                  isActive: isActiveDropTarget,
                  isForbidden: isForbiddenDropTarget,
                  theme,
                })}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, track)}
              />
            )}
          </AnalyticsConsumer>
        </AnalyticsLayer>
      )
    }
    return null
  }

  // Content-mode column overlay. Renders when a content item is being
  // dragged and the cursor is over this column, using the same
  // `dropTarget` style as moment-mode so both modes share one visual
  // language. `pointerEvents: 'none'` lets the underlying `ContentStack`
  // keep receiving drag events (it owns the actual cell-level drop), so
  // this div is purely decorative.
  const { draggedContent } = useContentDragContext()
  const isHovered = useIsHoveredDate(formatDateKey(date))
  const contentColumnActive = Boolean(draggedContent && isHovered)
  // After-the-moment-end drops are intentionally allowed; only pre-moment
  // drops are forbidden. The trailing-calendar icon in the moment-card
  // content list still uses the `isAfter` field as a passive UI hint.
  const contentColumnForbidden = useMemo(() => {
    if (!draggedContent) return false
    const { isBefore } = isDateOutsideMomentRange(date, {
      scheduled_at: draggedContent.momentScheduledAt,
      scheduled_end_at: draggedContent.momentScheduledEndAt,
    })
    return isBefore
  }, [draggedContent, date])

  const renderContentDropTarget = () => {
    if (!contentColumnActive) return null
    return (
      <div
        aria-hidden
        className={kuiperClassNames('dropTarget', 'contentDropTarget')}
        css={[
          styles.dropTarget({
            isActive: !contentColumnForbidden,
            isForbidden: contentColumnForbidden,
            theme,
          }),
          { pointerEvents: 'none' },
        ]}
      />
    )
  }

  const handleClick = () => {
    const params = new URLSearchParams({
      startDate: formatISO(date, { representation: 'date' }),
      ...selectedFilterParams,
    })

    /**
     * We use postMessage here to navigate the asgard router without triggering the
     * backbone router so the backbone view does not re-render needlessly.
     */
    parent.postMessage(
      {
        message: MessageKeys.execute,
        route: `/moments/${slug}/in_market_preview?${params.toString()}`,
        replace: false,
      },
      '*'
    )
  }

  return (
    <ThemeProvider theme={theme}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={kuiperClassNames(styles.selectors.root)}
        css={[styles.container, styles.inMarketPreview]}
        onClick={handleClick}
      >
        <Tooltip side="bottom" content={tooltipText} zIndex={999}>
          <p aria-hidden="true" css={styles.text}>
            <span className={kuiperClassNames(styles.selectors.date)}>
              {format(date, 'EEEE \u2022 d')}
            </span>
            <span
              css={styles.inMarketTrigger}
              className={kuiperClassNames(styles.selectors.inMarketTrigger)}
            >
              See In-Market Preview
            </span>
          </p>
        </Tooltip>
        <time
          aria-labelledby={htmlId.current}
          className="sr-only"
          dateTime={formatISO(date, { representation: 'date' })}
        >
          {format(date, 'EEEE LLLL do')}
        </time>
        {renderDropTargets()}
        {renderContentDropTarget()}
      </div>
    </ThemeProvider>
  )
}
WeekViewDayHeader.displayName = 'Continuum: WeekViewDayHeader ← WeekView'
